import React, { useState, useEffect, useContext, useMemo } from "react";
import { LocalesProvider, LocalesContext } from "./contexts/LocalesContext";
import SectionContentComponent from "./components/SectionContentComponent";
import SectionContentBlockComponent from "./components/SectionContentBlockComponent";
import ChartLineComponent from "./components/ChartLineComponent";
import SessionChartComponent from "./miplus/components/SessionChartComponent";
import {
  getDatesPerMinuteBetweenDates,
  timeToPrettyString,
} from "./utils/data";
import { getMachineParamsValue } from "./utils/irriapp";
import { getUnit, convertValue } from "./utils/unitsConverter";
import {
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  Line,
  Area,
  ResponsiveContainer,
} from "recharts";

// Miplus2 constants
const LABELS = {
  velocita: "Speed",
  qualita: "Quality",
  distanza_semina: "Seed distance",
  singulator: "Singulator",
  superficie_lavorata: "Worked area",
  produttivita: "Productivity",
  doppi: "Doubles",
  mancanti: "Missing",
  investimento: "Investment",
  depressione: "Vacuum",
  rpm: "RPM",
};

const COLORS = {
  velocita: "#f9ca24",
  qualita: "#6ab04c",
  distanza_semina: "#be2edd",
  singulator: "#22a6b3",
  superficie_lavorata: "#00b894",
  produttivita: "#6c5ce7",
  doppi: "#00cec9",
  mancanti: "#0984e3",
  investimento: "#535c68",
  depressione: "#e74c3c",
  rpm: "#2ecc71",
};

const getUnits = (isImperial) => ({
  velocita: getUnit('speed', isImperial),
  qualita: "%",
  distanza_semina: getUnit('distance', isImperial),
  singulator: 'pos',
  superficie_lavorata: getUnit('area', isImperial),
  produttivita: getUnit('productivity', isImperial),
  doppi: "%",
  mancanti: "%",
  investimento: getUnit('investment', isImperial),
  depressione: getUnit('pressure', isImperial),
  rpm: "rpm",
});

const B_LABELS = {
  snapshot: "Snapshot",
  events: "Events",
  alarmsIn: "Alarms in",
  alarmsOut: "Alarms out",
};

const B_COLORS = {
  snapshot: "#2c3e50",
  events: "#34495e",
  alarmsIn: "#e74c3c",
  alarmsOut: "#2ecc71",
};

const PAGINATION = 25;

function SessionWork(props) {
  const { t, actions: localesActions } = useContext(LocalesContext);
  const deviceType = props.device_type || 'irriapp';
  const isImperial = props.units_system === 'imperial';
  const UNITS = getUnits(isImperial);

  // Miplus2 specific state
  const [miplus2Session, setMiplus2Session] = useState(null);
  const [bar, setBar] = useState(1);
  const [fan, setFan] = useState(1);
  const [lines, setLines] = useState(['velocita', 'qualita', 'superficie_lavorata']);
  const [bars, setBars] = useState([]);
  const [page, setPage] = useState(1);
  const [viewType, setViewType] = useState('global');

  // Irriapp chart data
  const irriappChartData = useMemo(() => {
    if (deviceType !== 'irriapp' || props.session_snapshots.length < 1) return [];

    const chartKeys = [
      "work_tube_length",
      "work_water_used",
      "work_sector_speed",
      "work_sector_height",
    ];
    const chartBarKeys = ["event", "connection", "disconnection"];
    const chartAreaKeys = ["alarm"];

    const timeStart = new Date(props.session_snapshots[0].timestamp);
    const timeEnd = new Date(
      props.session_snapshots[props.session_snapshots.length - 1].timestamp
    );
    const minutes = getDatesPerMinuteBetweenDates(timeStart, timeEnd);

    const data = [];
    const dataPrev = {};
    
    minutes.map((minute, index) => {
      const snapshots = props.session_snapshots.filter((s) => {
        if (
          !chartKeys.includes(s.typology) &&
          !chartBarKeys.includes(s.typology) &&
          !chartAreaKeys.includes(s.typology)
        )
          return false;
        const snapshotTimestamp = new Date(s.timestamp);
        return (
          snapshotTimestamp >= minute &&
          (minutes[index + 1] ? snapshotTimestamp < minutes[index + 1] : true)
        );
      });

      const dataItem = { x: timeToPrettyString(minute) };
      snapshots.map((snapshot) => {
        if (dataItem[snapshot.typology]) return;

        if (chartBarKeys.includes(snapshot.typology)) {
          const chartBarLabels = [t("irriapp.labels.work_event"), t("irriapp.labels.work_connection"), t("irriapp.labels.work_disconnection")];
          const label = chartBarLabels[chartBarKeys.indexOf(snapshot.typology)];
          dataItem[snapshot.typology] = 1;
          dataItem[`${snapshot.typology}_string`] = snapshot.value_elaborated || label;
        } else if(chartAreaKeys.includes(snapshot.typology)) {
          const chartAreaLabels = [t("application.labels.alarm")];
          const label = chartAreaLabels[chartAreaKeys.indexOf(snapshot.typology)];
          dataItem[snapshot.typology] = 1;
          dataItem[`${snapshot.typology}_string`] = snapshot.value_elaborated || label;
        } else {
          // Converti i valori in base al sistema di unità
          let convertedValue = snapshot.value;
          if (snapshot.typology === 'work_tube_length') {
            convertedValue = convertValue(snapshot.value, 'distance', isImperial);
          } else if (snapshot.typology === 'work_water_used') {
            convertedValue = convertValue(snapshot.value, 'volume', isImperial);
          } else if (snapshot.typology === 'work_sector_speed') {
            convertedValue = convertValue(snapshot.value, 'linear_speed', isImperial);
          } else if (snapshot.typology === 'work_sector_height') {
            convertedValue = convertValue(snapshot.value, 'height', isImperial);
          }
          
          dataItem[snapshot.typology] = convertedValue;
          if (data[data.length - 1] && !data[data.length - 1][snapshot.typology]) {
            data[data.length - 1][snapshot.typology] = dataPrev[snapshot.typology];
          }
          dataPrev[snapshot.typology] = convertedValue;
        }
      });

      if(!dataItem['alarm']){
        dataItem['alarm'] = 0;
      }
      
      data.push(dataItem);
    });
    return data;
  }, [props.session_snapshots, t, deviceType, isImperial]);

  // Miplus2 chart data
  const translateAlarmCode = (code, subcode, t) => {
    const categoryTitle = t(`miplus2.alarms.${code}.title`);
    const alarmName = t(`miplus2.alarms.${code}.${subcode}`);
    
    // Se la traduzione non esiste, ritorna il formato numerico come fallback
    if (categoryTitle.includes('missing') || alarmName.includes('missing')) {
      return `CODE ${code}.${subcode}`;
    }
    
    return `${code}.${subcode} | ${categoryTitle} - ${alarmName}`;
  };

  const miplus2ChartData = useMemo(() => {
    if (deviceType !== 'miplus2' || !miplus2Session) return [];
    
    const _chartData = [];
    const timestampCreation = (new Date(props.session.timestamp_creation)).getTime() / 1000;
    const timestampEnd = props.session.timestamp_end ? (new Date(props.session.timestamp_end)).getTime() / 1000 : null;

    let contents = {};
    for (let i = 0; i < (timestampEnd - timestampCreation); i += 30) {
      contents[i] = {
        timestamp: secondsToString(i),
      };
    }

    miplus2Session.logs[miplus2Session.serials[0]].forEach((log, index) => {
      const seconds = log.timestamp - timestampCreation;
      const secondsKey = Math.floor(seconds / 30) * 30;
      if (!contents[secondsKey]) {
        contents[secondsKey] = {
          timestamp: secondsToString(secondsKey),
        };
      }

      if (log.event == 'work_data_update') {
        if (lines.includes('velocita')) {
          const rawValue = log.params.velocita_attuale ? (log.params.velocita_attuale / 1000) : 0;
          contents[secondsKey].velocita = round(convertValue(rawValue, 'speed', isImperial), 2);
          contents[secondsKey].velocita_note = log.params.velocita_tipo ? 'simulated' : 'real';
        }
        if (lines.includes('qualita')) {
          const value = log.params[`barra_${bar}_qualita_istantaneo`];
          contents[secondsKey].qualita = round(value ? value : 0, 2);
        }
        if (lines.includes('distanza_semina')) {
          const rawValue = log.params[`barra_${bar}_distanza_semina_istantaneo`];
          contents[secondsKey].distanza_semina = round(rawValue ? convertValue((rawValue / 10), 'distance', isImperial) : 0, 2);

          const rawTarget = log.params[`barra_${bar}_distanza_semina`];
          contents[secondsKey].distanza_semina_target = round(rawTarget ? convertValue((rawTarget / 10), 'distance', isImperial) : 0, 2);
        }
        if (lines.includes('singulator')) {
          contents[secondsKey].singulator = round(log.params.singulator ? log.params.singulator : 0, 2);
        }
        if (lines.includes('superficie_lavorata')) {
          const rawValue = log.params.superficie_lavorata ? (log.params.superficie_lavorata / 100) : 0;
          contents[secondsKey].superficie_lavorata = round(convertValue(rawValue, 'area', isImperial), 2);
        }
        if (lines.includes('produttivita')) {
          const rawValue = log.params.produttivita ? (log.params.produttivita / 10000000) : 0;
          contents[secondsKey].produttivita = round(convertValue(rawValue, 'productivity', isImperial), 2);
        }
        if (lines.includes('doppi')) {
          const value = log.params[`barra_${bar}_doppi_istantaneo`];
          contents[secondsKey].doppi = round(value ? value : 0, 2);
        }
        if (lines.includes('mancanti')) {
          const value = log.params[`barra_${bar}_mancanti_istantaneo`];
          contents[secondsKey].mancanti = round(value ? value : 0, 2);
        }
        if (lines.includes('investimento')) {
          const rawValue = log.params[`barra_${bar}_investimento_istantaneo`];
          contents[secondsKey].investimento = round(rawValue ? convertValue((rawValue / 10), 'investment', isImperial) : 0, 2);

          const rawTarget = log.params[`barra_${bar}_investimento`];
          contents[secondsKey].investimento_target = round(rawTarget ? convertValue((rawTarget / 10), 'investment', isImperial) : 0, 2);
        }
        if (lines.includes('depressione')) {
          const rawValue = log.params[`ventola_${fan}_depressione`];
          contents[secondsKey].depressione = round(rawValue ? convertValue(rawValue, 'pressure', isImperial) : 0, 2);
        }
        if (lines.includes('rpm')) {
          const value = log.params[`ventola_${fan}_rpm`];
          contents[secondsKey].rpm = round(value ? value : 0, 2);
        }
      } else if (log.event == 'work_status_update') {
        contents[secondsKey].events = 1;
        if (!contents[secondsKey]._events) contents[secondsKey]._events = [];
        contents[secondsKey]._events.push(`Status changed from ${log.params.work_status_from} to ${log.params.work_status_to}`);
      } else if (log.event == 'snapshot') {
        contents[secondsKey].snapshot = 1;
        contents[secondsKey]._snapshot = log.params;
      } else if (log.event == 'work_alarm_enter') {
        contents[secondsKey].alarmsIn = 1;
        if (!contents[secondsKey]._alarmsIn) contents[secondsKey]._alarmsIn = [];
        
        const translatedAlarm = translateAlarmCode(log.params.code, log.params.subcode, t);
        contents[secondsKey]._alarmsIn.push(`UNIT ${log.params.alarmUnit.toString(16)} | ${translatedAlarm} | PARAMS ${log.params.params.map((p) => p.toString(16))}`);
        
      } else if (log.event == 'work_alarm_exit') {
        contents[secondsKey].alarmsOut = 1;
        if (!contents[secondsKey]._alarmsOut) contents[secondsKey]._alarmsOut = [];
        
        const translatedAlarm = translateAlarmCode(log.params.code, log.params.subcode, t);
        contents[secondsKey]._alarmsOut.push(`UNIT ${log.params.alarmUnit.toString(16)} | ${translatedAlarm} | PARAMS ${log.params.params.map((p) => p.toString(16))}`);
      }
    });

    _chartData.push({
      timestamp: secondsToString(0),
      events: 1,
      _events: ['Start work']
    });

    Object.keys(contents).forEach(key => {
      _chartData.push(contents[key]);
    });

    if (timestampEnd) {
      _chartData.push({
        timestamp: secondsToString(timestampEnd - timestampCreation),
        events: 1,
        _events: ['End work']
      });
    }

    return _chartData;
  }, [bar, fan, lines, miplus2Session, deviceType, props.session, isImperial]);

  const totalPages = useMemo(() => {
    if (!miplus2ChartData) return 1;
    return Math.ceil(miplus2ChartData.length / PAGINATION);
  }, [miplus2ChartData]);

  const pageChartData = useMemo(() => {
    if (!miplus2ChartData) return [];
    if (viewType == 'global') return miplus2ChartData;

    const start = (page - 1) * PAGINATION;
    const end = start + PAGINATION;
    return miplus2ChartData.slice(start, end);
  }, [miplus2ChartData, page, viewType]);

  const avgData = useMemo(() => {
    const _avgData = {};
    Object.keys(LABELS).forEach(key => {
      if (key === 'superficie_lavorata') {
        // Per superficie_lavorata prendi l'ultimo valore
        let lastValue = 0;
        for (let i = pageChartData.length - 1; i >= 0; i--) {
          if (pageChartData[i][key]) {
            lastValue = pageChartData[i][key];
            break;
          }
        }
        _avgData[key] = round(lastValue, 2);
      } else {
        // Per tutti gli altri campi, calcola la media
        let total = 0;
        let count = 0;
        pageChartData.forEach(data => {
          if (data[key]) {
            total += data[key];
            count++;
          }
        });
        _avgData[key] = round(total / count, 2);
      }
    });

    return _avgData;
  }, [pageChartData]);

  // Effects
  useEffect(() => {
    localesActions.setLocale(props.locale);
    localesActions.setTranslations(props.translations);
  }, []);

  useEffect(() => {
    if (deviceType === 'miplus2') {
      if (props.params) {
        setMiplus2Session(props.params)
        console.log("Using params for miplus2 session data")
      } else if (props.data_json_url) {
        fetch(props.data_json_url)
        .then(response => response.json())
        .then(data => {
          setMiplus2Session(data)
          console.log("Using fetched data for miplus2 session data")
        })
      }
    }
  }, [deviceType, props.params, props.data_json_url]);

  // Miplus2 Components
  const CheckBox = ({ active, onChange, label, color }) => {
    return (
      <div
        style={{
          cursor: 'pointer',
          opacity: active ? 1 : 0.5,
          transition: 'opacity 0.2s ease',
          padding: '5px 10px',
          borderRadius: 5,
          backgroundColor: active ? color : '#fff',
          color: active ? '#fff' : color,
          border: `solid 1px ${color}`,
          marginRight: 5,
          marginBottom: 5,
          width: '100%',
          textAlign: 'center',
        }}
        onClick={onChange}
      >
        {label}
      </div>
    );
  };

  const TooltipItemAlarms = ({ item }) => {
    if (!item.payload._alarmsIn) return null;

    return (
      <div style={{ fontSize: 14, color: item.color }}>
        {item.payload._alarmsIn.map((alarm, index) => (
          <div key={`alarm-${index}`}>{alarm}</div>
        ))}
      </div>
    );
  };

  const TooltipItemEvents = ({ item }) => {
    if (!item.payload._events) return null;

    return (
      <div style={{ fontSize: 14, color: item.color }}>
        {item.payload._events.map((event, index) => (
          <div key={`event-${index}`}>{event}</div>
        ))}
      </div>
    );
  };

  const TooltipItemSnapshot = ({ item }) => {
    if (!item.payload._snapshot) return null;

    return (
      <div style={{ fontSize: 14, color: item.color }}>
        <div>Data snapshot</div>
        <div style={{ fontSize: 12, color: '#222222', marginTop: 5 }}>
          {Object.keys(item.payload._snapshot).map((key, index) => (
            <div key={`snapshot-${index}`}>{key}: {item.payload._snapshot[key]}</div>
          ))}
        </div>
      </div>
    );
  };

  const TooltipItem = ({ item }) => {
    const renderTarget = () => {
      const target = item.payload[`${item.name}_target`];
      if (!target) return null;
      return <span style={{ fontSize: 12, color: '#222222' }}>(target: {target} {UNITS[item.name]})</span>;
    };

    const renderNote = () => {
      const note = item.payload[`${item.name}_note`];
      if (!note) return null;

      return <span style={{ fontSize: 12, color: '#222222' }}>({note})</span>;
    };

    return (
      <div style={{ fontSize: 14, color: item.color, marginTop: 3 }}>
        {LABELS[item.name]}: {item.value} {UNITS[item.name]} {renderTarget()} {renderNote()}
      </div>
    );
  };

  const TooltipContent = ({ active, payload, label }) => {
    if (!active || !label || !payload) return null;

    const renderItem = (item) => {
      if (item.name == 'events') return <TooltipItemEvents item={item} />;
      if (item.name == 'snapshot') return <TooltipItemSnapshot item={item} />;
      if (item.name == 'alarmsIn' || item.name == 'alarmsOut') return <TooltipItemAlarms item={item} />;

      return <TooltipItem item={item} />;
    };

    return (
      <div style={{ backgroundColor: '#fff', border: 'solid 1px #D8D8D8', borderRadius: 5, padding: '5px 10px' }}>
        <time style={{ fontSize: 12, color: '#222222' }} className="custom-tooltip-time">{label}</time>

        <div style={{ marginTop: 5, paddingTop: 5, borderTop: 'solid 1px #D8D8D8' }}>
          {payload.map((item, index) => (
            <div key={`item-${index}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, color: '#222222', marginBottom: 10 }}>
              {renderItem(item)}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Work History Stats Component for Miplus2
  const WorkHistoryStats = () => {
    if (deviceType !== 'miplus2' || !avgData) return null;

    return (
      <div style={{ marginTop: 20, padding: 15, backgroundColor: '#f8f9fa', borderRadius: 8 }}>
        <h4 style={{ margin: '0 0 15px 0' }}>Work History Statistics</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 15 }}>
          {Object.keys(LABELS).map((key) => {
            if (!lines.includes(key) || isNaN(avgData[key])) return null;
            return (
              <div key={key} style={{ 
                padding: 10, 
                backgroundColor: '#fff', 
                borderRadius: 6,
                border: `2px solid ${COLORS[key]}`,
                textAlign: 'center'
              }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 5 }}>
                  {LABELS[key]}
                </div>
                <div style={{ fontSize: 18, fontWeight: 'bold', color: COLORS[key] }}>
                  {avgData[key]} {UNITS[key]}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render appropriate chart based on device type
  const renderChart = () => {
    if (deviceType === 'irriapp') {
      const chartKeys = ["work_tube_length", "work_water_used", "work_sector_speed", "work_sector_height"];
      const chartColors = ["#8e44ad", "#2980b9", "#f39c12", "#e67e22"];
      
      // Unità dinamiche basate sul sistema di unità
      const chartUnits = [
        getUnit('distance', isImperial),     // work_tube_length
        getUnit('volume', isImperial),       // work_water_used
        getUnit('linear_speed', isImperial), // work_sector_speed
        getUnit('height', isImperial)        // work_sector_height
      ];
      
      const chartLabels = [
        t("irriapp.labels.work_tube_length"),
        t("irriapp.labels.work_water_used"),
        t("irriapp.labels.work_sector_speed"),
        t("irriapp.labels.work_sector_height"),
      ];
      const chartBarKeys = ["event", "connection", "disconnection"];
      const chartBarColors = ["#2c3e50", "#2ecc71", "#c0392b"];
      const chartBarLabels = [t("irriapp.labels.work_event"), t("irriapp.labels.work_connection"), t("irriapp.labels.work_disconnection")];
      const chartAreaKeys = ["alarm"];
      const chartAreaColors = ["#c0392b"];
      const chartAreaLabels = [t("application.labels.alarm")];

      console.log(irriappChartData)

      if (irriappChartData.length > 0) {
        return (
          <ChartLineComponent
            data={irriappChartData}
            keys={chartKeys}
            colors={chartColors}
            units={chartUnits}
            labels={chartLabels}
            barKeys={chartBarKeys}
            barColors={chartBarColors}
            barLabels={chartBarLabels}
            areaKeys={chartAreaKeys}
            areaColors={chartAreaColors}
            areaLabels={chartAreaLabels}
          />
        );
      } else {
        return (
          <div className="c-message">
            <div className="c-message__content c-message__content--small">
              No data registered for this session
            </div>
          </div>
        );
      }
    }

    if (deviceType === 'miplus') {
      console.log(props.session_snapshots)

      if(props.session_snapshots.length > 0) {
      return (
        <SessionChartComponent 
          snap={props.session_snapshots} 
          unitsSystem={props.units_system} 
        />
      );
      } else {
        return (
          <div className="c-message">
            <div className="c-message__content c-message__content--small">
              No data registered for this session
            </div>
          </div>
        );
      }
    }

    if (deviceType === 'miplus2') {
      return (
        <div className="col-8">
          <ResponsiveContainer width="100%" height={550}>
            <ComposedChart data={pageChartData}>
              {lines.map((line) => (
                <Line
                  key={`Line-${line}`}
                  yAxisId={`YAxis-${line}`}
                  dataKey={line}
                  type="linear"
                  stroke={COLORS[line] || "#000000"}
                  name={line}
                  strokeWidth={3}
                  dot={{ strokeWidth: 2 }}
                  connectNulls
                  unit={UNITS[line]}
                />
              ))}
              {lines.map((line) => (
                <YAxis
                  key={`YAxis-${line}`}
                  yAxisId={`YAxis-${line}`}
                  dataKey={line}
                  stroke={COLORS[line] || "#000000"}
                  orientation="right"
                />
              ))}

              {bars.map((bar) => (
                <Bar
                  key={`Bar-${bar}`}
                  dataKey={bar}
                  barSize={20}
                  fill={B_COLORS[bar] || "#000000"}
                  yAxisId={`YAxis-bars`}
                />
              ))}
              <YAxis
                key={`YAxis-bars`}
                yAxisId={`YAxis-bars`}
                orientation="right"
                domain={[0, 10]}
                hide
              />

              <Tooltip content={<TooltipContent />} />
              <XAxis 
                dataKey="timestamp" 
                label={{ value: 'Duration', position: 'insideBottom', offset: -5 }} 
              />
              <CartesianGrid stroke="#efefef" />
            </ComposedChart>
          </ResponsiveContainer>
          
          {totalPages > 1 && viewType == 'zoom' && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: 10,
            }}>
              <button
                style={{
                  color: '#fff',
                  backgroundColor: '#000',
                  border: 'none',
                  padding: '5px 10px',
                  borderRadius: 5,
                  opacity: page == 1 ? 0.5 : 1,
                  cursor: page == 1 ? 'not-allowed' : 'pointer',
                }}
                disabled={page == 1}
                onClick={() => setPage(page - 1)}
              >Prev</button>
              <button
                style={{
                  color: '#fff',
                  backgroundColor: '#000',
                  border: 'none',
                  padding: '5px 10px',
                  borderRadius: 5,
                  opacity: totalPages == page ? 0.5 : 1,
                  cursor: totalPages == page ? 'not-allowed' : 'pointer',
                }}
                disabled={page == totalPages}
                onClick={() => setPage(page + 1)}
              >Next</button>
            </div> 
          )}
        </div>
      );
    }

    return <div>Tipo di dispositivo non supportato: {deviceType}</div>;
  };

  return (
    <SectionContentComponent>
    <div className="row row--ais">
      
          {renderChart()}

      <div className="col-2">   
      {deviceType === 'miplus2' && (
        <SectionContentBlockComponent sizes={{ default: 24, md: 6 }}>
            <h5 style={{ margin: 0, marginBottom: 10 }}>View type</h5>
            <div style={{ display: 'flex', justifyContent: 'stretch', marginBottom: 10 }}>
              {['global', 'zoom'].map((index) => (
                <CheckBox
                  key={`viewtype-${index}`}
                  active={viewType == index}
                  onChange={() => setViewType(index)}
                  label={index.toUpperCase()}
                  color={'#000'}
                />
              ))}
            </div>
            <h5 style={{ margin: 0, marginBottom: 10 }}>Boom</h5>
            <div style={{ display: 'flex', justifyContent: 'stretch', marginBottom: 10 }}>
              {[1, 2].map((index) => (
                <CheckBox
                  key={`bar-${index}`}
                  active={bar == index}
                  onChange={() => setBar(index)}
                  label={`Boom ${index}`}
                  color={'#000'}
                />
              ))}
            </div>
            <h5 style={{ margin: 0, marginBottom: 10 }}>Fan</h5>
            <div style={{ display: 'flex', justifyContent: 'stretch', marginBottom: 10 }}>
              {[1, 2, 3, 4, 5].map((index) => (
                <CheckBox
                  key={`fan-${index}`}
                  active={fan == index}
                  onChange={() => setFan(index)}
                  label={`Fan ${index}`}
                  color={'#000'}
                />
              ))}
            </div>
            <h5 style={{ margin: 0, marginBottom: 10 }}>Lines</h5>
            <div style={{ display: 'flex', justifyContent: 'stretch', flexWrap: 'wrap', marginBottom: 10 }}>
              {Object.keys(LABELS).map((key) => (
                <CheckBox
                  key={`data-${key}`}
                  active={lines.includes(key)}
                  onChange={() => setLines(lines.includes(key) ? lines.filter((line) => line != key) : [...lines, key])}
                  label={`${LABELS[key]} ${lines.includes(key) ? `(${key === 'superficie_lavorata' ? '' : 'AVG: '}${avgData[key] || 0} ${UNITS[key]})` : ''}`}
                  color={COLORS[key]}
                />
              ))}
            </div>
            <h5 style={{ margin: 0, marginBottom: 10 }}>Bars</h5>
            <div style={{ display: 'flex', justifyContent: 'stretch', flexWrap: 'wrap', marginBottom: 10 }}>
              {Object.keys(B_LABELS).map((key) => (
                <CheckBox
                  key={`data-${key}`}
                  active={bars.includes(key)}
                  onChange={() => setBars(bars.includes(key) ? bars.filter((bar) => bar != key) : [...bars, key])}
                  label={B_LABELS[key]}
                  color={B_COLORS[key]}
                />
              ))}
            </div>
        </SectionContentBlockComponent>
      )}
      </div>
    </div>
    </SectionContentComponent>
  );
}

export default (props) => {
  return (
    <LocalesProvider>
      <SessionWork {...props} />
    </LocalesProvider>
  );
};

// Helper functions
function secondsToString(seconds) {
  const date = new Date(0);
  date.setSeconds(seconds);
  return date.toISOString().substr(11, 8);
}

function round(value, decimals) {
  return Number(Math.round(value + 'e' + decimals) + 'e-' + decimals);
}